import { isEqual, noop, omit } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FilterDropdownItem,
  FilterToolbarGroup,
  SelectedFilters,
} from 'src/components/Filters';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { useDefaultSaleFilters } from 'src/components/Sales/SaleFilterBar/hooks/useDefaultSaleFilters';
import { useFilterToolbarQuickFilters } from 'src/hooks/useQuickFilters';
import { DefaultSaleQuery } from 'src/utils/eventQueryUtils';
import {
  CatalogResults,
  EventWithData,
  SaleMetrics,
  SaleQuery,
  UserSetting,
} from 'src/WebApiController';

import { useFilterQueryContext } from '../FilterQueryContext';
import { defaultEventSortingCriteria } from './MobileInventorySortAndFilterContext.constants';
import {
  InventoryEventSortingCriteria,
  InventorySortingCriteria,
} from './MobileInventorySortAndFilterContext.types';
import { sortEventWithCriteria } from './MobileInventorySortAndFilterContext.utils';
import { useMobileSalesFilters } from './MobileSalesSortAndFilterContext.hooks';

export type IMobileSalesSortAndFilterContext = {
  // Sorting props
  sortingCriteria: InventorySortingCriteria;
  hasSortingChanged: boolean;
  setSortingCriteria: (_: InventorySortingCriteria) => void;
  withEventSortingCriteria: (
    events: EventWithData[],
    eventMetrics?: Record<string, SaleMetrics>,
    catalogResult?: CatalogResults | undefined
  ) => EventWithData[];
  // Filter props
  tempQuery: SaleQuery;
  filters: FilterToolbarGroup[];
  selectedFilters: SelectedFilters;
  quickFilters?: QuickFilterToolbarItem[] | undefined;
  hasFilterApplied: boolean;
  canSaveFilter: boolean;
  setTempQuery: (_: SaleQuery) => void;
  onFilterRemoved: (_: FilterDropdownItem) => void;
  onSaveSelectedFilters: (_: SelectedFilters) => void;
  onSaveFilterQuery: () => void;
  onResetFilters: () => void;
  onResetCustomFilter: () => void;
};

const MobileSalesSortAndFilterContext =
  createContext<IMobileSalesSortAndFilterContext>({
    sortingCriteria: {} as InventorySortingCriteria,
    setSortingCriteria: noop,
    withEventSortingCriteria: (
      _: EventWithData[],
      __?: Record<string, SaleMetrics>,
      ___?: CatalogResults | undefined
    ) => [],
    hasSortingChanged: false,
    filters: [],
    selectedFilters: {},
    tempQuery: DefaultSaleQuery,
    hasFilterApplied: false,
    canSaveFilter: false,
    setTempQuery: noop,
    onFilterRemoved: noop,
    onSaveSelectedFilters: noop,
    onSaveFilterQuery: noop,
    onResetFilters: noop,
    onResetCustomFilter: noop,
  });

export const useMobileSalesSortAndFilterContext = () =>
  useContext(MobileSalesSortAndFilterContext);

export const MobileSalesSortAndFilterContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [sortingCriteria, setSortingCriteria] =
    useState<InventorySortingCriteria>(defaultEventSortingCriteria);

  const withEventSortingCriteria = useCallback(
    (
      events: EventWithData[],
      eventMetrics?: Record<string, SaleMetrics>,
      catalogResult?: CatalogResults | undefined
    ) => {
      const { sorting } = sortingCriteria as InventoryEventSortingCriteria;
      return events.toSorted(
        sortEventWithCriteria(sorting, eventMetrics, catalogResult)
      );
    },
    [sortingCriteria]
  );

  const hasSortingChanged = useMemo(() => {
    return !isEqual(sortingCriteria, defaultEventSortingCriteria);
  }, [sortingCriteria]);

  const {
    filterQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<SaleQuery>();

  useEffect(() => {
    if (!isEqual(filterQuery, tempQuery)) {
      setTempQuery(filterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const defaultQuickFilters = useDefaultSaleFilters({
    initialQuery,
    saleQuery: filterQuery,
  });

  const { quickFilters, onResetAll } = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStateSales,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomSales,
    defaultQuickFilters,
    currentQuery: filterQuery,
    initialQuery,
    onSelect: ({ query }) => {
      setFilterQuery(query);
    },
  });

  const { filters, selectedFilters, onFilterRemoved, onSaveSelectedFilters } =
    useMobileSalesFilters(
      initialQuery,
      filterQuery,
      tempQuery,
      setTempQuery,
      resetTempQuery
    );

  const onResetFilters = useCallback(() => {
    onSaveSelectedFilters({});
    setTempQuery(initialQuery);
  }, [initialQuery, onSaveSelectedFilters, setTempQuery]);

  const onSaveFilterQuery = useCallback(() => {
    setFilterQuery(tempQuery);
  }, [setFilterQuery, tempQuery]);

  const hasFilterApplied = useMemo(() => {
    const filteredFilters = omit(filterQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    const filteredDefaultFilters = omit(DefaultSaleQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    return !isEqual(filteredFilters, filteredDefaultFilters);
  }, [filterQuery]);

  const canSaveFilter = useMemo(
    () => !isEqual(filterQuery, tempQuery),
    [filterQuery, tempQuery]
  );

  return (
    <MobileSalesSortAndFilterContext.Provider
      value={{
        sortingCriteria,
        hasSortingChanged,
        setSortingCriteria,
        withEventSortingCriteria,
        tempQuery,
        filters,
        quickFilters,
        selectedFilters,
        hasFilterApplied,
        canSaveFilter,
        setTempQuery,
        onFilterRemoved,
        onSaveSelectedFilters,
        onSaveFilterQuery,
        onResetFilters,
        onResetCustomFilter: onResetAll,
      }}
    >
      {children}
    </MobileSalesSortAndFilterContext.Provider>
  );
};
